import { Download } from "lucide-react";
import { exportRows } from "../lib/api";

const PRIO = { High: "bg-emerald-600 text-white", Medium: "bg-amber-500 text-white", Low: "bg-slate-200 text-slate-700" };
const STATE = {
  operational: "bg-emerald-50 text-emerald-700 border-emerald-200", tested_ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
  success: "bg-emerald-50 text-emerald-700 border-emerald-200", sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  approved: "bg-blue-50 text-blue-700 border-blue-200", partial: "bg-amber-50 text-amber-700 border-amber-200",
  pending_approval: "bg-amber-50 text-amber-800 border-amber-200", draft_ready: "bg-amber-50 text-amber-800 border-amber-200",
  configured_untested: "bg-blue-50 text-blue-700 border-blue-200", not_yet_tested: "bg-blue-50 text-blue-700 border-blue-200",
  awaiting_credentials: "bg-slate-100 text-slate-700 border-slate-200", not_configured: "bg-slate-100 text-slate-600 border-slate-200",
  failed: "bg-red-50 text-red-700 border-red-200", test_failed: "bg-red-50 text-red-700 border-red-200", bounced: "bg-red-50 text-red-700 border-red-200",
  unsupported_platform_restriction: "bg-zinc-100 text-zinc-600 border-zinc-200", not_yet_implemented: "bg-zinc-100 text-zinc-600 border-zinc-200",
};
const EVID = {
  confirmed_requirement: ["Confirmed requirement", "bg-emerald-600 text-white"],
  public_hiring_signal: ["Public hiring signal", "bg-blue-600 text-white"],
  inferred_potential_requirement: ["Inferred potential requirement", "bg-amber-500 text-white"],
  unverified_assumption: ["Unverified assumption", "bg-slate-400 text-white"],
};

export const PriorityBadge = ({ p, testid }) => (
  <span data-testid={testid} className={`inline-flex rounded px-2 py-0.5 text-[11px] font-semibold ${PRIO[p] || PRIO.Low}`}>{p}</span>
);
export const StateBadge = ({ s, testid }) => (
  <span data-testid={testid} className={`inline-flex whitespace-nowrap rounded border px-2 py-0.5 text-[11px] font-medium ${STATE[s] || "bg-slate-50 text-slate-600 border-slate-200"}`}>
    {(s || "unknown").replaceAll("_", " ")}
  </span>
);
export const EvidenceBadge = ({ t }) => {
  const [label, cls] = EVID[t] || [t, "bg-slate-300"];
  return <span className={`inline-flex shrink-0 rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cls}`}>{label}</span>;
};
export const ScorePill = ({ score }) => (
  <span className={`font-mono text-sm font-semibold ${score >= 75 ? "text-emerald-700" : score >= 50 ? "text-amber-600" : "text-slate-500"}`}>{score}</span>
);

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-4 fade-up">
    <div>
      <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">{title}</h1>
      {subtitle && <p className="mt-1 max-w-3xl text-sm text-slate-500">{subtitle}</p>}
    </div>
    <div className="flex flex-wrap items-center gap-2">{children}</div>
  </div>
);

export const Card = ({ title, children, className = "", action, testid }) => (
  <div data-testid={testid} className={`rounded-lg border border-slate-200 bg-white shadow-sm ${className}`}>
    {title && (
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>{action}
      </div>
    )}
    <div className="p-4">{children}</div>
  </div>
);

export const Empty = ({ text, testid }) => <div data-testid={testid} className="py-10 text-center text-sm text-slate-500">{text}</div>;

export const ExportButtons = ({ rows, name }) => (
  <>
    <button data-testid={`export-${name}-csv`} onClick={() => exportRows(rows || [], name, "csv")} className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-sm hover:bg-slate-50 transition-colors">
      <Download size={14} /> CSV
    </button>
    <button data-testid={`export-${name}-xlsx`} onClick={() => exportRows(rows || [], name, "xlsx")} className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-sm hover:bg-slate-50 transition-colors">
      <Download size={14} /> Excel
    </button>
  </>
);

export const Btn = ({ children, variant = "primary", className = "", ...p }) => {
  const v = { primary: "bg-blue-600 text-white hover:bg-blue-700", dark: "bg-slate-900 text-white hover:bg-slate-800",
    ghost: "border border-slate-200 bg-white hover:bg-slate-50 text-slate-700", danger: "bg-red-600 text-white hover:bg-red-700" }[variant];
  return <button {...p} className={`inline-flex h-9 items-center justify-center gap-1.5 rounded-md px-3.5 text-sm font-medium transition-colors duration-200 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 ${v} ${className}`}>{children}</button>;
};

export const Sel = ({ value, onChange, options, testid, placeholder }) => (
  <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="dm-input pr-8">
    {placeholder !== undefined && <option value="">{placeholder}</option>}
    {options.map((o) => (typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.value} value={o.value}>{o.label}</option>))}
  </select>
);
